/**
 * Operator tenant (partner) detail page — a single partner's identity, its real
 * KPIs (hotels / calls 24h / plan), its hotels as a ledger, and its recent
 * calls. Renders ONLY canonical operator data from `@hostesa/api-client` (gated
 * by `admin:cross_tenant_read`); the call list is diagnostics only (no
 * transcript/guest content). Mirrors the prototype's data-first layout and the
 * calm loading / error / empty styling of the oversight list pages.
 */

import {
  type OperatorHotel,
  type TenantStatus,
  useOperatorCalls,
  useOperatorHotels,
  useOperatorTenant,
} from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  cn,
  EmptyState,
  ErrorState,
  LanguagePill,
  LedgerTable,
  LoadingState,
  MetricCard,
  type PillTone,
  StatusPill,
} from '@hostesa/ui';
import { PhoneIncoming, PhoneOutgoing } from 'lucide-react';

const PAGE_WRAP = 'mx-auto w-full max-w-6xl px-4 py-6 sm:px-8 sm:py-8';
const ROW = cn(
  'flex w-full flex-wrap items-center gap-3 border-b border-[var(--color-line-2)] px-3 py-3 last:border-0',
);
const ICON =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-canvas)] text-[var(--color-stone-600)]';
const META = 'font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]';
const EYEBROW =
  'mb-1 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)]';
const SECTION_EYEBROW =
  'mb-3 flex items-center gap-2.5 font-[var(--font-mono)] text-[10px] uppercase tracking-[0.14em] text-[var(--color-stone-mid)] before:h-px before:w-[18px] before:bg-[var(--color-line)]';

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function tenantTone(status: TenantStatus): PillTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'alert';
  return 'neutral';
}
function hotelTone(status: OperatorHotel['status']): PillTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'alert';
  return 'neutral';
}
function callTone(status: string): PillTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'alert';
  if (status === 'handoff_requested' || status === 'human_active') return 'accent';
  return 'neutral';
}
function toSeconds(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function langCode(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().toUpperCase() : null;
}

export interface TenantDetailPageProps {
  tenantId: string;
  /** Optional click handler for a hotel row (router wires navigation). */
  onSelectHotel?: (hotelId: string) => void;
  /** Optional back handler (router wires navigation). */
  onBack?: () => void;
}

export function TenantDetailPage({ tenantId, onSelectHotel, onBack }: TenantDetailPageProps) {
  const { t } = useT('backoffice');
  const { formatDuration } = useFormatters(VIEWER_TZ);

  const tenant = useOperatorTenant(tenantId);
  const hotels = useOperatorHotels({ tenantId });
  const calls = useOperatorCalls({ tenantId });

  if (tenant.isLoading) {
    return (
      <section data-testid="tenant-detail-page" className={PAGE_WRAP}>
        <LoadingState data-testid="tenant-detail-loading" title={t('oversight.loading')} />
      </section>
    );
  }
  if (tenant.isError || !tenant.data) {
    return (
      <section data-testid="tenant-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid="tenant-detail-error"
          title={t('oversight.error.title')}
          description={t('oversight.error.description')}
        />
      </section>
    );
  }

  const tn = tenant.data;
  const hotelRows = hotels.data?.items ?? [];
  const callRows = calls.data?.items ?? [];

  return (
    <section data-testid="tenant-detail-page" className={PAGE_WRAP}>
      {/* Header --------------------------------------------------------------- */}
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={EYEBROW}>
            {`${t('oversight.tenantDetail.eyebrow')} › ${t('oversight.tenantDetail.crumb')}`}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-[var(--font-display)] text-2xl text-[var(--color-charcoal)]">
              {tn.name}
            </h1>
            <StatusPill
              dot
              tone={tenantTone(tn.status)}
              label={t(`oversight.tenants.status.${tn.status}`)}
            />
          </div>
          <p className="mt-1 text-sm text-[var(--color-stone-mid)]">
            {`${tn.slug} · ${t(`oversight.tenants.plan.${tn.plan}`)}`}
          </p>
        </div>
        {onBack && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="tenant-detail-back-btn"
            onClick={onBack}
          >
            {t('oversight.tenantDetail.backToTenants')}
          </Button>
        )}
      </header>

      {/* KPI row -------------------------------------------------------------- */}
      <div data-testid="tenant-detail-kpis" className="mb-6 grid gap-4 sm:grid-cols-3">
        <MetricCard
          data-testid="tenant-detail-kpi-hotels"
          label={t('oversight.tenantDetail.kpi.hotels')}
          value={tn.hotelsCount}
        />
        <MetricCard
          data-testid="tenant-detail-kpi-calls24h"
          label={t('oversight.tenantDetail.kpi.calls24h')}
          value={tn.calls24h}
        />
        <MetricCard
          data-testid="tenant-detail-kpi-plan"
          label={t('oversight.tenantDetail.kpi.plan')}
          value={<span className="text-xl">{t(`oversight.tenants.plan.${tn.plan}`)}</span>}
        />
      </div>

      {/* Hotels -------------------------------------------------------------- */}
      <p className={SECTION_EYEBROW}>{t('oversight.tenantDetail.hotelsTitle')}</p>
      <Card className="mb-6 p-2 sm:p-3">
        {hotelRows.length === 0 ? (
          <EmptyState
            data-testid="tenant-detail-hotels-empty"
            icon={<PhoneIncoming />}
            title={t('oversight.tenantDetail.hotelsEmpty')}
          />
        ) : (
          <LedgerTable
            data-testid="tenant-detail-hotel"
            rows={hotelRows}
            getRowKey={(h) => h.id}
            onRowClick={onSelectHotel ? (h) => onSelectHotel(h.id) : undefined}
            columns={[
              {
                id: 'hotel',
                header: t('oversight.columns.hotel'),
                width: 'minmax(0,2fr)',
                cell: (h) => (
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate font-medium text-[var(--color-charcoal)]">
                      {h.name}
                    </span>
                    <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                      {h.slug}
                    </span>
                  </div>
                ),
              },
              {
                id: 'calls24h',
                header: t('oversight.columns.calls24h'),
                align: 'right',
                width: '90px',
                cell: (h) => h.calls24h,
              },
              {
                id: 'status',
                header: t('oversight.columns.status'),
                width: '120px',
                cell: (h) => (
                  <StatusPill
                    dot
                    tone={hotelTone(h.status)}
                    label={t(`oversight.hotels.status.${h.status}`)}
                  />
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Recent calls -------------------------------------------------------- */}
      <p className={SECTION_EYEBROW}>{t('oversight.tenantDetail.callsTitle')}</p>
      <Card className="p-2 sm:p-3">
        {callRows.length === 0 ? (
          <EmptyState
            data-testid="tenant-detail-calls-empty"
            icon={<PhoneIncoming />}
            title={t('oversight.tenantDetail.callsEmpty')}
          />
        ) : (
          <ul data-testid="tenant-detail-calls" className="flex flex-col">
            {callRows.map((c) => {
              const dur = toSeconds(c.durationSeconds);
              const lang = langCode(c.language);
              const DirectionIcon = c.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
              const context = c.hotelSlug ?? '';
              return (
                <li key={c.id}>
                  <div data-testid={`tenant-detail-call-row-${c.id}`} className={ROW}>
                    <span aria-hidden="true" className={ICON}>
                      <DirectionIcon className="size-4" strokeWidth={1.3} />
                    </span>
                    <StatusPill
                      dot
                      tone={callTone(c.status)}
                      label={t(`oversight.calls.status.${c.status}`)}
                    />
                    <span
                      className={cn(META, 'min-w-0 flex-1 truncate')}
                      title={context ? `${c.id} · ${context}` : c.id}
                    >
                      {c.id}
                      {context ? ` · ${context}` : ''}
                    </span>
                    {lang && <LanguagePill code={lang} />}
                    <span className={cn(META, 'w-14 text-right')}>
                      {dur !== null ? formatDuration(dur) : t('oversight.notAvailable')}
                    </span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </section>
  );
}
